import { push } from '@lagunovsky/redux-react-router'
import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { startLoading, stopLoading } from 'components/Loader/LoaderPopup/reducer'
import { setErrorMessage, setSuccessMessage } from 'components/Windows/CentralMessagesList/reducer'
import { setGoPayVariant, setPayment } from 'pages/OrderSummary/reducer'
import { callApiAsync } from 'services/api/callAPIAsync'
import gtmRemoveTickets from 'services/gtm/gtmRemoveTickets'
import { findCinemaId, findEventId } from '../../global/helpers'
import { API_LANGUAGE_MAPPING } from '../../global/i18n/constants'
import { localeSelector } from '../../global/i18n/selectors'
import { getCinemaSlug } from '../../pages/Hall/helpers'
import Api from '../../services/api'
import { ALLOWED_PAYMENTS } from './constants'
import {
    addTickets,
    addTicketsSuccess,
    basketGetDataRequestFailed,
    basketRelease,
    getData,
    initGiftVoucherBasket,
    postMealVoucher,
    reInitGiftVoucherBasket,
    removeCard,
    removeTicket,
    removeTicketSuccess,
    setBasketData,
    setBasketLoading,
    setCard,
    setCardProlonge,
    setMeals,
    ticketVoucherAdd,
} from './reducer'

function* getDataGql(item) {
    const API = new Api()
    const movieId = item.event.title_groupid
    const site = getCinemaSlug(item.event.cinemaid)
    try {
        const responseGql = yield call([API, 'getGqlEventData'], { movieId, site })
        const entry = responseGql?.data?.data?.data?.entry

        const posterUrl = entry?.PictureUrl ?? entry?.movie[0]?.PictureUrl ?? entry?.movieGroup[0]?.PictureUrl

        const dataGql = {
            genres: entry?.movie[0]?.CSFD[0]?.Genres,
            imageUrl: entry?.movie[0]?.CSFD[0]?.Photos[0]?.Url,
            movieSubtitles: entry?.movie[0]?.MovieSubtitles,
            posterUrl: posterUrl,
        }

        const updatedItem = {
            ...item,
            event: {
                ...item.event,
                ...dataGql,
            },
        }

        return updatedItem
    } catch (error) {
        console.error(error)
        const dataGql = {
            imageUrl: '',
            posterUrl: '',
        }
        const updatedItem = {
            ...item,
            event: {
                ...item.event,
                ...dataGql,
            },
        }

        return updatedItem
    }
}

export function* getBasketCallSaga() {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    yield put(setBasketLoading(true))

    const { data, error } = yield call(callApiAsync, 'getBasketData', { language })

    if (error) {
        yield put(setBasketLoading(false))
        yield put(basketGetDataRequestFailed())
        console.error(error)
        return
    }

    const dataGql = yield all(data?.items_tickets?.map((item) => getDataGql(item)))

    const basketDataWithGql = { ...data, items_tickets: dataGql }

    yield put(setBasketData(basketDataWithGql))
    yield put(setBasketLoading(false))
}

function* addTicketsCallSaga(action) {
    const ticketIds = action?.payload
    const currency = 'CZK'

    if (_.isEmpty(ticketIds)) {
        console.error('NO_TICKET_ID')
        yield put(setErrorMessage('NO_TICKET_ID'))
        return
    }

    const { data, error } = yield call(callApiAsync, 'addTicketsToBasket', { ticketids: ticketIds, currency })

    yield put(setBasketLoading(false))

    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }

    yield put(getData())
    yield put(addTicketsSuccess(data))
}

function* removeTicketCallSaga({ payload }) {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const ticketid = [...payload]

    yield call(gtmRemoveTickets, ticketid)

    yield put(startLoading())
    const { data, error } = yield call(callApiAsync, 'removeTicketFromBasket', { language, ticketid })
    yield put(stopLoading())

    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }

    yield put(removeTicketSuccess())
    yield put(getData(language))

    const emptyBasket = yield !data?.items_tickets?.length

    if (emptyBasket) {
        const referer = localStorage.getItem('referer')
        const eventid = yield select((state) => findEventId(state))
        const cinemaid = yield select((state) => findCinemaId(state))
        const site = getCinemaSlug(cinemaid)

        yield put(push(`/?eventid=${eventid}&site=${site}&ref=${referer}`))
    }
}

function* basketReleaseCallSaga() {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    const { data, error } = yield call(callApiAsync, 'cartRelease', { language })
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setBasketData(data))
}

function* setMealVoucherSaga({ payload }) {
    yield put(startLoading())
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const { data, error } = yield call(callApiAsync, 'setVoucher', { language, ...payload })
    yield put(stopLoading())
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setMeals(data))
}

function* setCardProlongeSaga() {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const cinemaid = yield select((state) => findCinemaId(state))
    const { data, error } = yield call(callApiAsync, 'setCardProlonge', { language, cinemaid })
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setCard(data))
}

function* setCardRemoveSaga({ payload }) {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const { data, error } = yield call(callApiAsync, 'setCardRemove', { language, ...payload })
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setCard(data))
}

function* ticketVoucherAddSaga({ payload }) {
    if (payload.voucher_number.length === 0) {
        yield put(setErrorMessage('NO_TICKET_VOUCHER'))
        return
    }
    yield put(startLoading())
    const { data, error } = yield call(callApiAsync, 'ticketVoucherAdd', payload)
    yield put(stopLoading())
    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    if (data.status_code !== 0) {
        yield put(setErrorMessage(`VOUCHER_ERROR_${data.status_code}`))
        return
    }
    yield put(setSuccessMessage('GIFT_VOUCHER_SUCCESS'))
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    yield put(getData(language))
}

function* reInitGiftVoucherBasketSaga({ payload }) {
    yield call(basketReleaseCallSaga)
    const voucherid = payload
    const cinemaid = localStorage.getItem('cinemaid')
    const args = { cinemaid, vouchers: [{ voucherid, amount: 1 }] }
    yield call(setMealVoucherSaga, { payload: args })
}

function* initGiftVoucherBasketSaga({ payload }) {
    const voucherid = payload
    const cinemaid = localStorage.getItem('cinemaid')
    const args = { cinemaid, vouchers: [{ voucherid, amount: 1 }] }
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    const { data, error } = yield call(callApiAsync, 'setVoucher', { language, ...args })

    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }
    yield put(setMeals(data))
    yield put(setGoPayVariant('Card'))
    yield put(setPayment(ALLOWED_PAYMENTS.GOPAY))
}

export default function* basketSaga() {
    yield all([
        takeLatest(getData.type, getBasketCallSaga),
        takeLatest(addTickets.type, addTicketsCallSaga),
        takeLatest(removeTicket.type, removeTicketCallSaga),
        takeLatest(basketRelease.type, basketReleaseCallSaga),
        takeLatest(postMealVoucher.type, setMealVoucherSaga),
        takeLatest(setCardProlonge.type, setCardProlongeSaga),
        takeLatest(removeCard.type, setCardRemoveSaga),
        takeLatest(ticketVoucherAdd.type, ticketVoucherAddSaga),
        takeLatest(reInitGiftVoucherBasket.type, reInitGiftVoucherBasketSaga),
        takeLatest(initGiftVoucherBasket.type, initGiftVoucherBasketSaga),
    ])
}
